import {
  Bug,
  Building2,
  CircleX,
  FileStack,
  HandCoins,
  IdCard,
  MailWarning,
  MapPinOff,
  Scale,
  SearchX,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Combobox } from "@/components/combobox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

import { useClaim } from "../api/use-claim";
import { useAbandonTypes, useSetClaimAbandonType } from "../api/use-claim-actions";
import { getAiEligibilityVerdict } from "../lib/ai-verdict";

/** Icon per abandon type id — mirrors SelectAbandonType.vue. */
const abandonTypeIconById: Record<number, { Icon: typeof X; className: string }> = {
  1: { Icon: CircleX, className: "text-red-600" }, // CX
  2: { Icon: MapPinOff, className: "text-violet-600" }, // HC
  3: { Icon: Building2, className: "text-gray-600" }, // ENTERPRISE_DEACTIVATED
  4: { Icon: Bug, className: "text-orange-600" }, // BUG_ALGO
  5: { Icon: X, className: "text-zinc-600" }, // NOT_ELIGIBLE
  6: { Icon: Scale, className: "text-pink-600" }, // LOST_CASE
  7: { Icon: FileStack, className: "text-amber-600" }, // DUPLICATE
  8: { Icon: HandCoins, className: "text-teal-600" }, // ALREADY_COMPENSATED
  10: { Icon: MailWarning, className: "text-blue-600" }, // COMPENSATION_PROCEDURE_EXISTING
  11: { Icon: IdCard, className: "text-purple-600" }, // MISSING_PASSENGER_DOCUMENT
  12: { Icon: SearchX, className: "text-orange-600" }, // ALGO_FALSE_POSITIVE
};

const NOT_ELIGIBLE_ABANDON_TYPE_ID = 5; // "Cas non-éligible"
const FALSE_POSITIVE_ABANDON_TYPE_ID = 12; // ALGO_FALSE_POSITIVE — "Faux positif"

/**
 * Abandoning as "faux positif" / "cas non-éligible" a claim the AI study found eligible
 * contradicts the AI: the jurist's explanation is what feeds the study back (TECH-967).
 */
export function isAbandonContradictingAi(
  abandonTypeId: number | undefined,
  aiVerdict: boolean | undefined,
): boolean {
  return (
    aiVerdict === true &&
    (abandonTypeId === NOT_ELIGIBLE_ABANDON_TYPE_ID || abandonTypeId === FALSE_POSITIVE_ABANDON_TYPE_ID)
  );
}

interface AbandonTypeOption {
  id: number;
  displayName: string;
}

interface AbandonTypeComboboxProps {
  value: AbandonTypeOption | null;
  onChange: (abandonType: AbandonTypeOption | null) => void;
  disabled?: boolean;
}

/** « Type d'abandon » picker (icon per type), shared by the single and batch abandon flows. */
export function AbandonTypeCombobox({ value, onChange, disabled }: AbandonTypeComboboxProps) {
  const { data: abandonTypes = [] } = useAbandonTypes();

  return (
    <Combobox
      items={abandonTypes}
      value={value}
      onChange={onChange}
      getKey={(type) => type.id}
      getLabel={(type) => type.displayName}
      renderItem={(type) => {
        const { Icon, className } = abandonTypeIconById[type.id] ?? {
          Icon: X,
          className: "text-red-600",
        };
        return (
          <>
            <Icon className={cn("h-4 w-4", className)} />
            <span className={cn("ml-1 truncate", value?.id === type.id && "font-semibold")}>
              {type.displayName}
            </span>
          </>
        );
      }}
      placeholder="Aucun"
      searchPlaceholder="Rechercher..."
      emptyMessage="Aucun type trouvé"
      unselectLabel="Aucun"
      limit={20}
      disabled={disabled}
    />
  );
}

interface SelectAbandonTypeProps {
  claimId: string;
  currentAbandonType?: AbandonTypeOption | null;
  /** Preselects this type when the claim has none yet — submission still requires the confirm click. */
  defaultAbandonTypeId?: number;
  onUpdated?: () => void;
}

/**
 * Port of ui/select/SelectAbandonType.vue — shared abandon flow: pick an
 * abandon type, add an internal note, then send the "Abandon" machine event.
 */
export function SelectAbandonType({
  claimId,
  currentAbandonType,
  defaultAbandonTypeId,
  onUpdated,
}: SelectAbandonTypeProps) {
  const { can } = useCurrentUser();
  const { data: abandonTypes = [] } = useAbandonTypes();
  const { data: claim } = useClaim(claimId);
  const setClaimAbandonType = useSetClaimAbandonType(claimId);

  // `undefined` = untouched by the user; the preselection then applies once
  // abandon types are loaded (`null` = explicitly cleared).
  const [pickedAbandonType, setPickedAbandonType] = useState<AbandonTypeOption | null | undefined>(undefined);
  const selectedAbandonType =
    pickedAbandonType !== undefined
      ? pickedAbandonType
      : (currentAbandonType ?? abandonTypes.find((type) => type.id === defaultAbandonTypeId) ?? null);
  const [internalNote, setInternalNote] = useState("");

  const canEdit = can([...UPCLAIM_ROLES, "CUSTOMER_ADMIN", "CUSTOMER_ACCOUNT_MANAGER"]);
  const noteRequired = isAbandonContradictingAi(selectedAbandonType?.id, getAiEligibilityVerdict(claim));
  const noteMissing = noteRequired && !internalNote.trim();

  async function confirmChanges() {
    if (!selectedAbandonType || noteMissing) {
      return;
    }
    const { success } = await setClaimAbandonType.mutateAsync({
      abandonTypeId: selectedAbandonType.id,
      internalNote,
    });
    if (success) {
      onUpdated?.();
    }
  }

  return (
    <div className="px-6 text-left">
      <div className="space-y-4">
        <div>
          <Label className="mb-2 block text-sm font-medium leading-6 text-gray-900">Type d'abandon</Label>
          <AbandonTypeCombobox
            value={selectedAbandonType}
            onChange={setPickedAbandonType}
            disabled={!canEdit}
          />
        </div>

        <div>
          <Label htmlFor="internal-note" className="block text-sm font-medium leading-6 text-gray-900">
            Note explicative{noteRequired ? " *" : null}
          </Label>
          {noteRequired ? (
            <p className="mt-1 text-sm text-amber-700">
              L'étude IA juge ce dossier éligible : expliquez pourquoi vous l'abandonnez.
            </p>
          ) : null}
          <div className="mt-2">
            <Textarea
              id="internal-note"
              rows={3}
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder={
                noteRequired
                  ? "Ce qui rend le dossier non éligible malgré l'étude : cause, dates, itinéraire…"
                  : "Ajouter une note interne..."
              }
              required={noteRequired}
              aria-invalid={noteMissing || undefined}
              disabled={!canEdit}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            variant="destructive"
            size="lg"
            className="text-sm"
            onClick={confirmChanges}
            disabled={!selectedAbandonType || noteMissing || setClaimAbandonType.isPending}
          >
            <Trash2 className="text-white" />
            Abandonner le dossier
          </Button>
        </div>
      </div>
    </div>
  );
}
